import { env } from "@/config/env";

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
}

export interface ChatConversation {
  id: string;
  userId: string | null;
  openaiThreadId: string | null;
  createdAt: string;
  updatedAt: string;
  messages: ChatMessage[];
}

export interface CreateConversationResponse {
  message: string;
  data: {
    id: string;
    userId: string | null;
    openaiThreadId: string | null;
    createdAt: string;
    updatedAt: string;
    messages: ChatMessage[];
  };
}

export interface SendMessageResponse {
  message: string;
  data: ChatConversation;
}

export interface GetHistoryResponse {
  message: string;
  data: ChatConversation | null;
}

/**
 * Create a new chat conversation
 */
export async function createConversation(): Promise<CreateConversationResponse> {
  const response = await fetch(`${env.apiUrl}/api/v1/chat/conversations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: "Failed to create conversation",
    }));

    // Handle rate limiting
    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    throw new Error(errorData.message || "Failed to create conversation");
  }

  return response.json();
}

/**
 * Send a chat message
 */
export async function sendMessage(
  conversationId: string,
  content: string
): Promise<SendMessageResponse> {
  const response = await fetch(`${env.apiUrl}/api/v1/chat/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      conversationId,
      content,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: "Failed to send message",
    }));

    // Handle validation errors
    if (response.status === 400 && errorData.message) {
      throw new Error(errorData.message);
    }

    // Handle rate limiting
    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    throw new Error(errorData.message || "Failed to send message");
  }

  return response.json();
}

/**
 * Get chat conversation history
 */
export async function getHistory(conversationId: string): Promise<GetHistoryResponse> {
  const response = await fetch(`${env.apiUrl}/api/v1/chat/history/${conversationId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      message: "Failed to get history",
    }));
    throw new Error(errorData.message || "Failed to get history");
  }

  return response.json();
}
